import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useModuleNavigation } from './useModuleNavigation';
import type { NavigationItem } from '@/contexts/ModuleNavigationContext';

/**
 * Options for useRegisterModuleNavigation hook
 */
export interface UseRegisterModuleNavigationOptions {
  /** Module identifier (kebab-case) */
  moduleId: string;
  /** Navigation items */
  items: NavigationItem[];
  /** Optional display name for accessibility */
  displayName?: string;
  /** Enable/disable registration (default: true) */
  enabled?: boolean;
}

/**
 * Hook for automatic module navigation registration based on routes
 *
 * Automatically registers module navigation when the current route matches
 * the module's path prefix, and cleans up on unmount.
 *
 * @param options - Configuration options for module navigation
 *
 * @example
 * ```tsx
 * const OKRDashboard = () => {
 *   useRegisterModuleNavigation({
 *     moduleId: 'okr',
 *     displayName: 'OKR',
 *     items: [
 *       { name: 'Dashboard', href: '/okr', icon: HomeIcon, exact: true },
 *       { name: 'Objetivos', href: '/okr/objectives', icon: FlagIcon },
 *     ]
 *   });
 *
 *   return <div>{/\* Page content *\/}</div>;
 * };
 * ```
 */
export function useRegisterModuleNavigation(options: UseRegisterModuleNavigationOptions): void {
  const { setModuleNav } = useModuleNavigation();
  const location = useLocation();

  // Memoize navigation config to prevent unnecessary re-renders
  const navigationConfig = useMemo(() => ({
    moduleId: options.moduleId,
    items: options.items,
    displayName: options.displayName,
    isActive: true
  }), [options.moduleId, options.items, options.displayName]);

  useEffect(() => {
    // Skip if disabled
    if (options.enabled === false) {
      return;
    }

    // Check if current route matches this module
    const isActive = location.pathname.startsWith(`/${options.moduleId}`);

    if (isActive) {
      setModuleNav(navigationConfig);
    }

    // Cleanup on unmount or when route changes away from this module
    return () => {
      setModuleNav(null);
    };
  }, [
    location.pathname,
    options.moduleId,
    options.enabled,
    navigationConfig,
    setModuleNav
  ]);
}
